import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Close, CompletedSolid, ErrorBadgeSolid } from "@gaming/xds-react-js-icons";

import * as styles from "./HubNotification.module.css";

const exitAnimationMs = 180;
const notificationRegionId = "hub-notification-region";

const getNotificationRegion = () => {
  const existing = document.getElementById(notificationRegionId);
  if (existing) {
    return existing;
  }

  const region = document.createElement("div");
  region.id = notificationRegionId;
  region.className = styles.notificationRegion;
  document.body.appendChild(region);
  return region;
};

type HubNotificationProps = Readonly<{
  actionLabel?: string;
  autoDismissMs?: number;
  closeLabel: string;
  description?: string;
  kind: "error" | "success";
  onAction?: () => void;
  onClose: () => void;
  title: string;
}>;

export const HubNotification = ({
  actionLabel,
  autoDismissMs,
  closeLabel,
  description,
  kind,
  onAction,
  onClose,
  title,
}: HubNotificationProps) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const isLeavingRef = useRef(false);
  const exitTimeout = useRef<number>();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const close = useCallback(() => {
    if (isLeavingRef.current) {
      return;
    }
    isLeavingRef.current = true;
    setIsLeaving(true);
    exitTimeout.current = window.setTimeout(() => onCloseRef.current(), exitAnimationMs);
  }, []);

  useEffect(() => {
    if (!autoDismissMs) {
      return;
    }

    const timeout = window.setTimeout(close, autoDismissMs);
    return () => window.clearTimeout(timeout);
  }, [autoDismissMs, close]);

  useEffect(
    () => () => {
      if (exitTimeout.current) {
        window.clearTimeout(exitTimeout.current);
      }
    },
    []
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className={`${styles.notification} ${kind === "success" ? styles.success : styles.error} ${
        isLeaving ? styles.leaving : ""
      }`}
      role={kind === "error" ? "alert" : "status"}
    >
      <Icon
        aria-hidden="true"
        className={styles.statusIcon}
        icon={kind === "success" ? CompletedSolid : ErrorBadgeSolid}
        iconSize={IconSize.Custom}
      />
      <div className={styles.content}>
        <strong>{title}</strong>
        {description && <span>{description}</span>}
      </div>
      {actionLabel && onAction && (
        <BasicButton as="button" buttonType={ButtonType.Secondary} type="button" onClick={onAction}>
          {actionLabel}
        </BasicButton>
      )}
      <button type="button" className={styles.close} aria-label={closeLabel} onClick={close}>
        <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
      </button>
    </div>,
    getNotificationRegion()
  );
};
